import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Stack } from '@mui/material';

import ResponseRateIcon from 'src/assets/dashboard/response_rate_icon.svg';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import { useCustomizing } from 'src/components/app/context/CustomizingContext';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import Column from 'src/components/dashboard/DashboardPage/components/Column';
import DiffColumn from 'src/components/dashboard/DashboardPage/components/DiffColumn';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import CellWithBar from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget/CellWithBar';
import ResponseManagerDownloadButton from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget/DownloadButton';
import { transformData } from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget/helper';
import InvitedColumn from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget/InvitedColumn';
import RespondentsColumn from 'src/components/dashboard/DashboardPage/widgets/ResponseManagerWidget/RespondentsColumn';
import { getComparisonData } from 'src/components/dashboard/helper';

import { styles } from './index.styles';

export const RESPONSE_MANAGER_TEST_ID = 'response-manager';

const ResponseManager: FC<DashboardWidgetProps> = ({ config }) => {
  const { data, loading, errorMsg, refetch } = useStatistics();
  const { t } = useTranslation('dashboard');
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  const {
    labels,
    rates,
    diffs,
    respondents,
    invited,
    returns_particip_email,
    returns_particip_qr_code,
    returns_particip_code,
    returns_particip_sc,
    returns_particip_zebra,
    returns_particip_email_rate,
    returns_particip_qr_code_rate,
    returns_particip_zebra_rate,
    returns_particip_code_rate,
    returns_particip_sc_rate,
  } = useMemo(
    () => transformData(data ? getComparisonData(config, data) : []),
    [config, data],
  );
  const { currentApp } = useCustomizing();
  const hideDownloadButton = !!currentApp?.hide_response_manager_download;
  const columnData = [
    {
      headerKey: 'Labels.ParticipationChannelEmail',
      data: returns_particip_email,
    },
    {
      headerKey: 'Labels.ParticipationChannelEmailRate',
      data: returns_particip_email_rate,
    },
    {
      headerKey: 'Labels.ParticipationChannelPWLQRCode',
      data: returns_particip_qr_code,
    },
    {
      headerKey: 'Labels.ParticipationChannelPWLQRCodeRate',
      data: returns_particip_qr_code_rate,
    },
    {
      headerKey: 'Labels.ParticipationChannelPWLCode',
      data: returns_particip_code,
    },
    {
      headerKey: 'Labels.ParticipationChannelPWLCodeRate',
      data: returns_particip_code_rate,
    },
    {
      headerKey: 'Labels.ParticipationChannelSmartConnect',
      data: returns_particip_sc,
    },
    {
      headerKey: 'Labels.ParticipationChannelSmartConnectRate',
      data: returns_particip_sc_rate,
    },
    {
      headerKey: 'Labels.ParticipationChannelZebra',
      data: returns_particip_zebra,
    },
    {
      headerKey: 'Labels.ParticipationChannelZebraRate',
      data: returns_particip_zebra_rate,
    },
  ].filter((column) => column.data.length);

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-response-manager"
          sx={{ p: 2, position: 'relative' }}
        >
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={styles.responseManager} data-test-id={RESPONSE_MANAGER_TEST_ID}>
      {!!guidedTour && <Box>{guidedTour}</Box>}
      <Column
        alignment="left"
        header={
          !hideDownloadButton && (
            <ResponseManagerDownloadButton widgetId={config.id} />
          )
        }
        data={labels.map((label, i) => (
          <Box className="response-manager-label" key={i}>
            {label}
          </Box>
        ))}
        sx={styles.firstColumn}
      />
      <Column
        growing
        header={
          <>
            <img src={ResponseRateIcon} alt="response rate icon" width={40} />
            {t('Labels.ResponseRate')}
          </>
        }
        data={rates.map((rate, i) => (
          <CellWithBar percentage={rate} key={i} />
        ))}
      />
      <DiffColumn diffs={diffs} />
      <RespondentsColumn respondents={respondents} />
      <InvitedColumn invited={invited} />

      {columnData.map(({ headerKey, data }, index) => (
        <Column
          key={index}
          header={<>{t(headerKey)}</>}
          data={data}
          growing
          minWidth="200px"
          sx={{ whiteSpace: 'normal' }}
        />
      ))}
    </Box>
  );
};

export default ResponseManager;
